import { api } from "@/lib/api";

let streamP = null;

// Full-band capture: 48 kHz mono, no browser noise suppression (it filters out speech detail); echo cancel + gain stay on
const MIC_CONSTRAINTS = { audio: { sampleRate: { ideal: 48000 }, sampleSize: { ideal: 16 }, channelCount: { ideal: 1 }, echoCancellation: true, noiseSuppression: false, autoGainControl: true } };
export const AUDIO_BITRATE = 160000;

// One shared mic stream per session: avoids re-prompting and start-up lag between items
export const getMicStream = () => {
  if (streamP) return streamP;
  if (!navigator.mediaDevices?.getUserMedia) return Promise.reject(Object.assign(new Error("unsupported"), { name: "NotSupportedError" }));
  streamP = navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS)
    .then((s) => { s.getTracks().forEach((t) => { t.onended = () => { streamP = null; }; }); return s; })
    .catch((e) => { streamP = null; throw e; });
  return streamP;
};

export const releaseMic = () => {
  if (!streamP) return;
  streamP.then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {});
  streamP = null;
};

export const micErrorKind = (e) => (e?.name === "NotAllowedError" || e?.name === "SecurityError" ? "denied" : e?.name === "NotFoundError" ? "nodevice" : "error");

const pickMime = () => {
  if (!window.MediaRecorder) return "";
  return ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
};

// Starts recording on the shared stream; returns a controller with live level and stop()
let activeRecs = 0;
let idleTimer = null;
let lastStop = Promise.resolve();
let sharedCtx = null;
// Mic turns off after 20s without a recording, so back-to-back items don't re-prompt or lag
const scheduleRelease = () => { clearTimeout(idleTimer); idleTimer = setTimeout(() => { if (activeRecs === 0) releaseMic(); }, 20000); };
const audioCtx = () => {
  if (!sharedCtx || sharedCtx.state === "closed") sharedCtx = new (window.AudioContext || window.webkitAudioContext)();
  return sharedCtx;
};
// Call from a click handler (Start / Enable / I heard it) so the browser allows audio processing
export const warmAudio = () => { try { audioCtx().resume().catch(() => {}); } catch { /* unsupported */ } };
const ensureRunning = async (ctx) => {
  if (ctx.state === "running") return true;
  await Promise.race([ctx.resume().catch(() => {}), new Promise((r) => setTimeout(r, 1500))]);
  return ctx.state === "running";
};
const liveStream = (s) => !!s && s.active && s.getAudioTracks().some((t) => t.readyState === "live");

let lastStart = Promise.resolve();
// Starts are serialized: a new recorder is created only after any earlier start has settled and its recorder stopped
export const startRecording = (onLevel) => {
  const prev = lastStart;
  const p = (async () => {
    await prev.catch(() => {});
    await Promise.resolve();
    await lastStop;
    return doStart(onLevel);
  })();
  lastStart = p;
  return p;
};

const doStart = async (onLevel) => {
  clearTimeout(idleTimer);
  let stream = await getMicStream();
  // A stream stopped elsewhere can't record: reopen the mic before starting
  if (!liveStream(stream)) { releaseMic(); stream = await getMicStream(); }
  if (!liveStream(stream)) throw Object.assign(new Error("Microphone stream not active"), { name: "NotReadableError" });
  activeRecs++;
  let keepMic = false;
  const mime = pickMime();
  const ctx = audioCtx();
  let wasSuspended = !(await ensureRunning(ctx));
  const rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: AUDIO_BITRATE });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
  const resume = () => ctx.state === "suspended" && ctx.resume().catch(() => {});
  const onState = () => { if (ctx.state !== "running") wasSuspended = true; };
  ctx.addEventListener("statechange", onState);
  window.addEventListener("pointerdown", resume);
  window.addEventListener("keydown", resume);
  const an = ctx.createAnalyser(); an.fftSize = 1024;
  const src = ctx.createMediaStreamSource(stream); src.connect(an);
  const buf = new Float32Array(an.fftSize);
  let peak = 0, voiced = 0, raf;
  const tick = () => {
    an.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    peak = Math.max(peak, rms);
    if (rms > 0.012) voiced++;
    onLevel?.(Math.min(100, rms * 600));
    raf = requestAnimationFrame(tick);
  };
  tick();
  rec.start(250);
  let stopped = null;
  let chunksOut = [];
  return {
    stop() {
      if (stopped) return stopped;
      cancelAnimationFrame(raf); onLevel?.(0);
      stopped = new Promise((res) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          // a suspended analyser can't measure voice, so never drop the recording in that case
          const measured = !wasSuspended && ctx.state === "running";
          window.removeEventListener("pointerdown", resume); window.removeEventListener("keydown", resume);
          ctx.removeEventListener("statechange", onState);
          src.disconnect();
          chunksOut = chunks.splice(0);
          // Free the input only after the last chunk is flushed, and only if no newer recording uses it
          activeRecs = Math.max(0, activeRecs - 1);
          if (!keepMic && activeRecs === 0) scheduleRelease();
          res({ blob: chunksOut.length ? new Blob(chunksOut, { type: rec.mimeType || mime || "audio/webm" }) : null, peak, voiced: measured ? voiced : Infinity });
        };
        if (rec.state === "inactive") return finish();
        rec.addEventListener("stop", finish, { once: true });
        setTimeout(finish, 3000);
        rec.stop();
      });
      lastStop = stopped.then(() => {}, () => {});
      return stopped;
    },
    // Discard without touching the shared mic (cancelled/remounted items)
    abort() { keepMic = true; return this.stop(); },
  };
};

const extFor = (type) => (type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : "webm");

// Sends audio to the backend Whisper endpoint; silent recordings skip the call
export const transcribe = async ({ blob, voiced }) => {
  if (!blob || blob.size < 1200 || voiced < 3) return "";
  const fd = new FormData();
  fd.append("file", blob, `answer.${extFor(blob.type)}`);
  for (let attempt = 0; attempt < 2; attempt++) {
    try { const { data } = await api.post("/stt", fd, { timeout: 45000 }); return data.text || ""; }
    catch (e) { if (attempt || e?.response?.status === 413) throw e; }
  }
  return "";
};
